import React, { useState, useEffect } from 'react';
import { getStudents, getTeachers, addUser, deleteUser, getClasses, assignStudent } from '../../api';
import { toast } from 'react-toastify';
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000';

const ManageUsers = () => {
  const [activeTab, setActiveTab] = useState('students');
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'student',
    phone: '', address: ''
  });
  const [profilePic, setProfilePic] = useState(null);
  const [assignData, setAssignData] = useState({
    class_id: '', roll_number: '', session: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [s, t, c] = await Promise.all([getStudents(), getTeachers(), getClasses()]);
      setStudents(s.data);
      setTeachers(t.data);
      setClasses(c.data);
    } catch (err) {
      toast.error('Error loading data');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.keys(formData).forEach(k => data.append(k, formData[k]));
      if (profilePic) data.append('profile_pic', profilePic);
      await addUser(data);
      toast.success('User added successfully!');
      setShowAddForm(false);
      setFormData({ name: '', email: '', password: '', role: 'student', phone: '', address: '' });
      loadData();
    } catch (err) {
      toast.error('Error adding user');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await deleteUser(id);
        toast.success('User deleted!');
        loadData();
      } catch (err) {
        toast.error('Error deleting user');
      }
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await assignStudent({ student_id: selectedStudent, ...assignData });
      toast.success('Student assigned to class!');
      setShowAssignForm(false);
    } catch (err) {
      toast.error('Error assigning student');
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h3 style={styles.title}>Manage Users</h3>
        <button style={styles.addBtn} onClick={() => setShowAddForm(!showAddForm)}>
          + Add New User
        </button>
      </div>

      {showAddForm && (
        <div style={styles.formCard}>
          <h4 style={styles.formTitle}>Add New User</h4>
          <form onSubmit={handleAddUser} style={styles.form}>
            <div style={styles.formGrid}>
              <input style={styles.input} placeholder="Full Name" value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})} required />
              <input style={styles.input} placeholder="Email" type="email" value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})} required />
              <input style={styles.input} placeholder="Password" type="password" value={formData.password}
                onChange={e => setFormData({...formData, password: e.target.value})} required />
              <select style={styles.input} value={formData.role}
                onChange={e => setFormData({...formData, role: e.target.value})}>
                <option value="student">Student</option>
                <option value="teacher">Teacher</option>
              </select>
              <input style={styles.input} placeholder="Phone" value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})} />
              <input style={styles.input} placeholder="Address" value={formData.address}
                onChange={e => setFormData({...formData, address: e.target.value})} />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Profile Picture:</label>
              <input type="file" accept="image/*"
                onChange={e => setProfilePic(e.target.files[0])} />
            </div>
            <div style={styles.btnGroup}>
              <button type="submit" style={styles.addBtn}>Save User</button>
              <button type="button" style={styles.cancelBtn}
                onClick={() => setShowAddForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {showAssignForm && (
        <div style={styles.formCard}>
          <h4 style={styles.formTitle}>Assign Student to Class</h4>
          <form onSubmit={handleAssign} style={styles.form}>
            <div style={styles.formGrid}>
              <select style={styles.input} value={assignData.class_id}
                onChange={e => setAssignData({...assignData, class_id: e.target.value})} required>
                <option value="">Select Class</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name} {c.section} - {c.group_name}</option>
                ))}
              </select>
              <input style={styles.input} placeholder="Roll Number" value={assignData.roll_number}
                onChange={e => setAssignData({...assignData, roll_number: e.target.value})} />
              <input style={styles.input} placeholder="Session (e.g. 2024-2026)" value={assignData.session}
                onChange={e => setAssignData({...assignData, session: e.target.value})} />
            </div>
            <div style={styles.btnGroup}>
              <button type="submit" style={styles.addBtn}>Assign</button>
              <button type="button" style={styles.cancelBtn}
                onClick={() => setShowAssignForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div style={styles.tabs}>
        <button style={{...styles.tab, ...(activeTab==='students' ? styles.tabActive : {})}}
          onClick={() => setActiveTab('students')}>
          Students ({students.length})
        </button>
        <button style={{...styles.tab, ...(activeTab==='teachers' ? styles.tabActive : {})}}
          onClick={() => setActiveTab('teachers')}>
          Teachers ({teachers.length})
        </button>
      </div>

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>Photo</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(activeTab === 'students' ? students : teachers).map(u => (
              <tr key={u.id} style={styles.tableRow}>
                <td style={styles.td}>
                  {u.profile_pic
                    ? <img src={`${BASE_URL}/uploads/${u.profile_pic}`}
                        alt="" style={styles.avatar} />
                    : <div style={styles.avatarPlaceholder}>
                        {u.name?.charAt(0).toUpperCase()}
                      </div>
                  }
                </td>
                <td style={styles.td}>{u.name}</td>
                <td style={styles.td}>{u.email}</td>
                <td style={styles.td}>{u.phone || '-'}</td>
                <td style={styles.td}>
                  <span style={{...styles.badge,
                    background: u.is_active ? '#d4edda' : '#f8d7da',
                    color: u.is_active ? '#155724' : '#721c24'}}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  {activeTab === 'students' && (
                    <button style={styles.assignBtn} onClick={() => {
                      setSelectedStudent(u.id);
                      setShowAssignForm(true);
                    }}>Assign Class</button>
                  )}
                  <button style={styles.deleteBtn}
                    onClick={() => handleDelete(u.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { color: '#1a3a5c', margin: 0 },
  addBtn: { padding: '10px 20px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  cancelBtn: { padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  formCard: { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  formTitle: { color: '#1a3a5c', margin: '0 0 15px' },
  form: { display: 'flex', flexDirection: 'column', gap: '15px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' },
  input: { padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', width: '100%' },
  inputGroup: { display: 'flex', alignItems: 'center', gap: '10px' },
  label: { fontSize: '14px', fontWeight: '600', color: '#333' },
  btnGroup: { display: 'flex', gap: '10px' },
  tabs: { display: 'flex', gap: '10px', marginBottom: '15px' },
  tab: { padding: '10px 20px', background: 'white', border: '2px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  tabActive: { background: '#0d6efd', color: 'white', borderColor: '#0d6efd' },
  tableCard: { background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#1a3a5c' },
  th: { padding: '12px 15px', color: 'white', textAlign: 'left', fontSize: '13px' },
  tableRow: { borderBottom: '1px solid #eee' },
  td: { padding: '12px 15px', fontSize: '13px', color: '#444' },
  avatar: { width: '35px', height: '35px', borderRadius: '50%', objectFit: 'cover' },
  avatarPlaceholder: { width: '35px', height: '35px', borderRadius: '50%', background: '#0d6efd', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' },
  badge: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  assignBtn: { padding: '5px 10px', background: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', marginRight: '5px' },
  deleteBtn: { padding: '5px 10px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' },
};

export default ManageUsers;